import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays, MapPin, CheckCircle2, Users, Send, Clock, AlertCircle,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, postForm } from '../../api/client';
import Logo from '../../components/Logo';
import { Button, Field, Input, Select, Textarea, Badge, Loader } from '../../components/UI';
import { fmtDate, fmtDateTime, EVENT_TYPE_LABEL } from '../../utils/format';

/**
 * The public sign-up form. It is the only route that works without a login, so
 * it carries its own layout rather than sitting inside the portal shell.
 */
export default function PublicRegistration() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/forms/public/${slug}`);

  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [problem, setProblem] = useState('');

  if (loading) return <Shell><Loader full label="Loading the registration form" /></Shell>;

  if (error || !data) {
    return (
      <Shell>
        <Centered
          icon={AlertCircle}
          title="This registration link isn't available"
          message={error || 'The link may have expired, or registration for this event has closed.'}
        />
      </Shell>
    );
  }

  const { event, seatsLeft, isFull } = data;
  // The organiser's builder owns every question now, ordering included.
  const fields = [...(data.fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const submit = async (e) => {
    e.preventDefault();
    setProblem('');

    const missing = fields.filter((f) => {
      if (!f.required) return false;
      if (f.type === 'file') return !files[f.key];
      const v = answers[f.key];
      return Array.isArray(v) ? v.length === 0 : !String(v ?? '').trim();
    });
    if (missing.length) return setProblem(`Please fill in: ${missing.map((f) => f.label).join(', ')}`);

    setBusy(true);
    try {
      // Attachments force multipart; everything else rides as plain JSON.
      const attachments = Object.entries(files).filter(([, f]) => f);
      let res;
      if (attachments.length) {
        const body = new FormData();
        body.append('responses', JSON.stringify(answers));
        attachments.forEach(([key, file]) => body.append(key, file));
        res = await postForm(`/forms/public/${slug}`, body);
      } else {
        res = await post(`/forms/public/${slug}`, { responses: answers });
      }
      setDone({ message: res.message, ...res.data });
    } catch (err) {
      setProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <Centered
          icon={CheckCircle2}
          tone="var(--ok)"
          title="You're registered"
          message={done.message}
          extra={
            <div style={{
              marginTop: 24, padding: 18, maxWidth: 420,
              background: 'var(--ink-850)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
            }}>
              {done.credentials ? (
                <>
                  <p style={{ fontSize: 12.5, color: 'var(--text-mid)', lineHeight: 1.7 }}>
                    These are your sign-in details. We've emailed them too, but
                    please save them now.
                  </p>
                  <div style={{
                    marginTop: 12, padding: 12, textAlign: 'left',
                    background: 'var(--ink-900)', border: '1px solid var(--border)',
                    borderRadius: 'var(--r)', fontSize: 13,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ color: 'var(--text-lo)' }}>Login ID</span>
                      <strong style={{ color: 'var(--gold-400)', wordBreak: 'break-all' }}>{done.credentials.loginId}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
                      <span style={{ color: 'var(--text-lo)' }}>Password</span>
                      <strong style={{ color: 'var(--gold-400)', fontFamily: 'monospace' }}>{done.credentials.password}</strong>
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-lo)', lineHeight: 1.7, marginTop: 12 }}>
                    Open the Golden Lotus app, choose "Sign in with your login ID",
                    and change your password afterwards.
                  </p>
                </>
              ) : (
                <p style={{ fontSize: 12.5, color: 'var(--text-mid)', lineHeight: 1.7 }}>
                  Download the Golden Lotus app and sign in with your mobile
                  number — we'll send you a code on WhatsApp to access your
                  digital pass, schedule and travel details.
                </p>
              )}
            </div>
          }
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={{ maxWidth: 660, margin: '0 auto', padding: '36px 22px 64px' }}>
        {/* ── event header ─────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          style={{
            padding: 26, marginBottom: 22, position: 'relative', overflow: 'hidden',
            background: event.banner
              ? `linear-gradient(180deg, rgba(11,11,12,0.25), rgba(11,11,12,0.92)), url(${event.banner}) center/cover`
              : 'linear-gradient(135deg, #15151B 0%, #1F1F27 55%, #121217 100%)',
            border: '1px solid var(--border)', borderRadius: 'var(--r-xl)',
          }}
        >
          <motion.div
            animate={{ opacity: [0.35, 0.65, 0.35] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', top: -60, right: -30, width: 280, height: 280,
              background: 'radial-gradient(circle, rgba(239,212,138,0.15), transparent 70%)',
              filter: 'blur(40px)', pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative' }}>
            <Badge tone="gold">{EVENT_TYPE_LABEL[event.type] || event.type}</Badge>
            <h1 style={{
              fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 600,
              lineHeight: 1.22, marginTop: 12,
            }}>
              {event.title}
            </h1>
            {event.description && (
              <p style={{ fontSize: 13.5, color: 'var(--text-mid)', marginTop: 11, lineHeight: 1.7 }}>
                {event.description}
              </p>
            )}
            <div style={{ display: 'flex', gap: 18, marginTop: 16, flexWrap: 'wrap', fontSize: 12.8, color: 'var(--text-mid)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <CalendarDays size={14} color="var(--gold-500)" />
                {fmtDate(event.startDate)} – {fmtDate(event.endDate)}
              </span>
              {(event.venue?.name || event.venue?.city) && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <MapPin size={14} color="var(--gold-500)" />
                  {event.venue?.name || event.venue?.city}
                </span>
              )}
              {seatsLeft !== null && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Users size={14} color="var(--gold-500)" />
                  {seatsLeft > 0 ? `${seatsLeft} seats left` : 'Waitlist only'}
                </span>
              )}
            </div>
            {data.closesAt && (
              <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
                <Clock size={12} /> Registration closes {fmtDateTime(data.closesAt)}
              </p>
            )}
          </div>
        </motion.div>

        {isFull && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{
              padding: 15, marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-start',
              background: 'var(--warn-dim)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 'var(--r)',
            }}>
            <AlertCircle size={18} color="var(--warn)" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6 }}>
              This event has reached capacity, so registration is closed. Please
              contact your event coordinator if you think you should have a place.
            </p>
          </motion.div>
        )}

        {/* ── form ─────────────────────────────────────────────── */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.5 }}
          style={{
            padding: 26, background: 'var(--surface)',
            border: '1px solid var(--border)', borderRadius: 'var(--r-xl)',
          }}
        >
          <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 5 }}>Register for this event</h2>
          <p style={{ fontSize: 12.8, color: 'var(--text-mid)', marginBottom: 24 }}>
            Fields marked <span style={{ color: 'var(--gold-400)' }}>*</span> are required.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {fields.map((f) => (
              <Field key={f.key} label={f.label} required={f.required} hint={f.hint}>
                {f.type === 'select' ? (
                  <Select value={answers[f.key] || ''} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })}>
                    <option value="">Select…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </Select>
                ) : f.type === 'textarea' ? (
                  <Textarea value={answers[f.key] || ''} onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} />
                ) : f.type === 'radio' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(f.options || []).map((o) => (
                      <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                        <input type="radio" name={f.key} checked={answers[f.key] === o}
                          onChange={() => setAnswers({ ...answers, [f.key]: o })}
                          style={{ accentColor: 'var(--gold-400)' }} />
                        {o}
                      </label>
                    ))}
                  </div>
                ) : f.type === 'file' ? (
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    padding: '11px 13px', fontSize: 13, borderRadius: 'var(--r)',
                    border: `1px dashed ${files[f.key] ? 'var(--gold-500)' : 'var(--border)'}`,
                    color: files[f.key] ? 'var(--gold-300)' : 'var(--text-mid)',
                  }}>
                    <input type="file" hidden accept="image/*,application/pdf"
                      onChange={(e) => setFiles({ ...files, [f.key]: e.target.files?.[0] || null })} />
                    {files[f.key] ? files[f.key].name : 'Choose a file (image or PDF)'}
                  </label>
                ) : f.type === 'checkbox' && f.options?.length ? (
                  /* Choices on a tick box mean a multi-select. */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {f.options.map((o) => {
                      const picked = Array.isArray(answers[f.key]) ? answers[f.key] : [];
                      const on = picked.includes(o);
                      return (
                        <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                          <input
                            type="checkbox" checked={on}
                            onChange={() => setAnswers({
                              ...answers,
                              [f.key]: on ? picked.filter((x) => x !== o) : [...picked, o],
                            })}
                            style={{ width: 15, height: 15, accentColor: 'var(--gold-400)' }}
                          />
                          {o}
                        </label>
                      );
                    })}
                  </div>
                ) : f.type === 'checkbox' ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={Boolean(answers[f.key])}
                      onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.checked })}
                      style={{ width: 15, height: 15, accentColor: 'var(--gold-400)' }} />
                    Yes
                  </label>
                ) : (
                  <Input type={f.type || 'text'} value={answers[f.key] || ''}
                    onChange={(e) => setAnswers({ ...answers, [f.key]: e.target.value })} />
                )}
              </Field>
            ))}

            <AnimatePresence>
              {problem && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  style={{
                    padding: '11px 14px', background: 'var(--danger-dim)',
                    border: '1px solid rgba(248,113,113,0.24)', borderRadius: 'var(--r)',
                    color: 'var(--danger)', fontSize: 12.8,
                  }}
                >
                  {problem}
                </motion.div>
              )}
            </AnimatePresence>

            <Button type="submit" size="lg" icon={Send} loading={busy} style={{ marginTop: 6 }}>
              {isFull ? 'Join the waitlist' : 'Submit registration'}
            </Button>

            <p style={{ fontSize: 11.5, color: 'var(--text-lo)', textAlign: 'center', lineHeight: 1.6 }}>
              By registering you agree to be contacted about this event on the
              email and WhatsApp number you provide.
            </p>
          </div>
        </motion.form>
      </div>

      <style>{`@media (max-width: 560px) { .gl-reg-row { grid-template-columns: 1fr !important; } }`}</style>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', position: 'relative', zIndex: 1 }}>
      <header style={{
        padding: '18px 24px', borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'center',
        background: 'rgba(11,11,12,0.82)', backdropFilter: 'blur(14px)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <Logo size={34} showText />
      </header>
      {children}
      <footer style={{ padding: '24px', textAlign: 'center', fontSize: 11.5, color: 'var(--text-lo)' }}>
        Powered by Golden Lotus · MICE Event Management &amp; Corporate Travel
      </footer>
    </div>
  );
}

function Centered({ icon: Icon, title, message, tone = 'var(--gold-400)', extra }) {
  return (
    <div style={{
      minHeight: '70vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 28, textAlign: 'center',
    }}>
      <motion.div
        initial={{ scale: 0 }} animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        style={{
          width: 72, height: 72, borderRadius: '50%', marginBottom: 22,
          background: 'rgba(239,212,138,0.1)', display: 'grid', placeItems: 'center',
        }}
      >
        <Icon size={32} color={tone} />
      </motion.div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 600 }}>{title}</h2>
      <p style={{ fontSize: 13.5, color: 'var(--text-mid)', marginTop: 10, maxWidth: 420, lineHeight: 1.7 }}>
        {message}
      </p>
      {extra}
    </div>
  );
}
